import type { DatiSensoreDinamicoDTO } from '@/models/dto/plc/dati-sensore-dinamico-dto'
import type { DatiSensoreStaticoDTO } from '@/models/dto/plc/dati-sensore-statico-dto'
import type { FotoDTO } from '@/models/dto/plc/foto-dto'
import type { FotoPaginationDTO } from '@/models/dto/plc/foto-pagination-dto'
import type { PLCBaseDTO } from '@/models/dto/plc/plc-base-dto'
import type { PLCDTO } from '@/models/dto/plc/plc-dto'
import type { PLCPaginationDTO } from '@/models/dto/plc/plc-pagination-dto'
import type { SensorePLCDTO } from '@/models/dto/plc/sensore-plc-dto'
import { mapToBasePLC } from '@/models/mappers/plc/base-plc-mapper'
import { mapToDynamicSensorReadings } from '@/models/mappers/plc/dynamic-sensor-readings-mapper'
import { mapToPhoto } from '@/models/mappers/plc/photo-mapper'
import { mapToPhotosWithPagination } from '@/models/mappers/plc/photos-mapper'
import { mapToPLC, mapToPlcDTO } from '@/models/mappers/plc/plc-mapper'
import { mapToPLCs } from '@/models/mappers/plc/plcs-mapper'
import { mapToSensorConfiguration } from '@/models/mappers/plc/sensor-configuration-mapper'
import { mapToStaticSensorReadings } from '@/models/mappers/plc/static-sensor-readings-mapper'
import type { AggretationStrategy } from '@/models/types/plc/aggregation-strategy'
import type { BasePLC } from '@/models/types/plc/base-plc'
import type { DynamicSensorReadings } from '@/models/types/plc/dynamic-sensor-readings'
import type { Photo } from '@/models/types/plc/photo'
import type { PhotosWithPagination } from '@/models/types/plc/photos-with-pagination'
import type { PLC } from '@/models/types/plc/plc'
import type { PLCsWithPagination } from '@/models/types/plc/plcs-with-pagination'
import type { Sensor } from '@/models/types/plc/sensor'
import type { SensorConfiguration } from '@/models/types/plc/sensor-configuration'
import type { StaticSensorReadings } from '@/models/types/plc/static-sensor-readings'
import type { OrderType } from '@/types/table'
import { capitalizeFirstLetter } from '@/utils/utils'
import type { FetchOptionsType } from './api'
import { deleteJsonRequest, getRequest, postJsonRequest, putJsonRequest } from './make-request'

type PLCGetOptions = {
    order?: OrderType
    sort?: keyof PLCDTO
    search?: string
    page?: string
} & Partial<Record<keyof PLC, string>>

type SensorGetOptions = {
    from?: string
    to?: string
    strategy?: AggretationStrategy
    onlyLastValue?: boolean
} & Partial<Record<keyof Sensor, string>>

type PhotoGetOptions = {
    order?: OrderType
    sort?: keyof PLCDTO
    search?: string
    page?: string
    timestampFrom?: string
    timestampTo?: string
} & Partial<Record<keyof Photo, string>>

type PLCCrudType = (fetchOptions?: FetchOptionsType) => {
    getOne: (id: number) => Promise<PLC>
    getOneByFacilityId: (id: number) => Promise<PLC>
    getAll: () => Promise<BasePLC[]>
    getMany: (itemsPerPage?: string, options?: PLCGetOptions, onlyEnabled?: boolean) => Promise<PLCsWithPagination>
    put: (PLC: PLC) => Promise<PLC>
    delete: (id: number) => Promise<void>
    getDynamicSensorReadings: (
        plcId: number,
        sensorId: number,
        options?: SensorGetOptions,
    ) => Promise<DynamicSensorReadings>
    getStaticSensorReadings: (
        plcId: number,
        sensorId: number,
        options?: SensorGetOptions,
    ) => Promise<StaticSensorReadings>
    getDigitalSensorReadings: (
        plcId: number,
        sensorId: number,
        options?: SensorGetOptions,
    ) => Promise<StaticSensorReadings>
    getSensorConfiguration: (sensorId: number, sensorName: string) => Promise<SensorConfiguration>
    getNewPhoto: (plcId: number) => Promise<Photo>
    getLastPhoto: (pldId: number) => Promise<Photo>
    getManyPhotos: (pldId: number, itemsPerPage?: string, options?: PhotoGetOptions) => Promise<PhotosWithPagination>
    putSensoriPredefiniti : (PLC : PLC) =>Promise<PLC>
    ripristina: (id: number) => Promise<void>
}

const PLC_URL = typeof window === 'undefined' ? `${process.env.API_URL}/api/v1/PLC` : `${location.origin}/api/v1/PLC`
const PLC_TAG = 'PLC'
const PHOTO_TAG = 'Photo'

const plcCRUD: PLCCrudType = (fetchOptions) => ({
    getOne: async (id) => {
        const url = new URL(`${PLC_URL}/${id.toString()}`)
        const plcDTO = await getRequest<PLCDTO>({ url, next: { tags: [PLC_TAG] }, ...fetchOptions })
        
        const plc = mapToPLC(plcDTO)
        return plc
    },
    getOneByFacilityId: async (id) => {
        const url = new URL(`${PLC_URL}/impianto/${id.toString()}`)
        const plcDTO = await getRequest<PLCDTO>({ url, next: { tags: [PLC_TAG] }, ...fetchOptions })
        const plc = mapToPLC(plcDTO)
        return plc
    },
    getAll: async () => {
        const url = new URL(PLC_URL)
        const plcDTOs = await getRequest<PLCBaseDTO[]>({ url, next: { tags: [PLC_TAG] }, ...fetchOptions })
        const plcs = plcDTOs.map(mapToBasePLC)
        return plcs
    },
    getMany: async (itemsPerPage, options, onlyEnabled = false) => {
        const url = new URL(`${PLC_URL}/Pagination`)
        url.searchParams.append('PageSize', itemsPerPage ?? '999999')
        options?.page && url.searchParams.append('PageIndex', options.page)
        options?.order && url.searchParams.append('Descending', options.order === 'desc' ? 'true' : 'false')
        options?.sort && url.searchParams.append('SortBy', capitalizeFirstLetter(options.sort))

        onlyEnabled && url.searchParams.append('OnlyActive', 'true')

        options?.search && url.searchParams.append('Filters[search]', options.search)

        options?.enabled && url.searchParams.append('Filter[Attivo]', options.enabled)
        options?.name && url.searchParams.append('Filters[Nome]', options.name)
        options?.status && url.searchParams.append('Filters[Stato]', options.status)
        options?.version && url.searchParams.append('Filters[Versione]', options.version)

        const plcsPaginationDTO = await getRequest<PLCPaginationDTO>({
            url,
            next: { tags: [PLC_TAG] },
            ...fetchOptions,
        })
        const plcsPagination = mapToPLCs(plcsPaginationDTO)
        return plcsPagination
    },
    put: async (plc) => {
        const plcDTO = mapToPlcDTO(plc)
        const url = new URL(PLC_URL)
        const plcsDTOResponse = await putJsonRequest<PLCDTO>({ url, body: plcDTO, ...fetchOptions })
        const newPLC = mapToPLC(plcsDTOResponse)
        return newPLC
    },
    delete: async (id) => {
        const url = new URL(`${PLC_URL}/${id.toString()}`)
        await deleteJsonRequest({ url, ...fetchOptions })
    },
    getSensorConfiguration: async (sensorId, sensorName) => {
        const url = new URL(`${PLC_URL}/sensore/${sensorId.toString()}/nome/${sensorName}/configurazione`)
        const sensorePLCDTO = await getRequest<SensorePLCDTO>({
            url,
            next: { tags: [PLC_TAG] },
            ...fetchOptions,
        })
        const sensorConfiguration = mapToSensorConfiguration(sensorePLCDTO)
        return sensorConfiguration
    },
    getDynamicSensorReadings: async (plcId, sensorId, options) => {
        const { from, to, strategy, onlyLastValue } = options || {}
        const url = new URL(`${PLC_URL}/${plcId.toString()}/SensoreDinamico/${sensorId.toString()}`)
        from && url.searchParams.append('timestampFrom', from)
        to && url.searchParams.append('timestampTo', to)
        // `strategy !== undefined` e non `strategy &&`: MAX vale 0, che e' falsy, quindi il
        // controllo precedente lo scartava e il parametro non partiva proprio.
        strategy !== undefined && url.searchParams.append('aggregationStrategy', strategy.toString())
        onlyLastValue && url.searchParams.append('lastValue', 'true')

        const readingsDTOs = await getRequest<DatiSensoreDinamicoDTO>({
            url,
            next: { tags: [PLC_TAG] },
            ...fetchOptions,
        })
        const readings = mapToDynamicSensorReadings(readingsDTOs)
        return readings
    },
    getStaticSensorReadings: async (plcId, sensorId, options) => {
        const { from, to, strategy, onlyLastValue } = options || {}
        const url = new URL(`${PLC_URL}/${plcId.toString()}/SensoreStatico/${sensorId.toString()}`)
        from && url.searchParams.append('timestampFrom', from)
        to && url.searchParams.append('timestampTo', to)
        // `strategy !== undefined` e non `strategy &&`: MAX vale 0, che e' falsy, quindi il
        // controllo precedente lo scartava e il parametro non partiva proprio.
        strategy !== undefined && url.searchParams.append('aggregationStrategy', strategy.toString())
        onlyLastValue && url.searchParams.append('lastValue', 'true')

        const readingsDTOs = await getRequest<DatiSensoreStaticoDTO>({
            url,
            next: { tags: [PLC_TAG] },
            ...fetchOptions,
        })
        const readings = mapToStaticSensorReadings(readingsDTOs)
        return readings
    },
    getDigitalSensorReadings: async (plcId, sensorId, options) => {
        const { from, to, strategy, onlyLastValue } = options || {}
        const url = new URL(`${PLC_URL}/${plcId.toString()}/SensoreDigitale/${sensorId.toString()}`)
        from && url.searchParams.append('timestampFrom', from)
        to && url.searchParams.append('timestampTo', to)
        // `strategy !== undefined` e non `strategy &&`: MAX vale 0, che e' falsy, quindi il
        // controllo precedente lo scartava e il parametro non partiva proprio.
        strategy !== undefined && url.searchParams.append('aggregationStrategy', strategy.toString())
        onlyLastValue && url.searchParams.append('lastValue', 'true')

        const readingsDTOs = await getRequest<DatiSensoreStaticoDTO>({
            url,
            next: { tags: [PLC_TAG] },
            ...fetchOptions,
        })
        const readings = mapToStaticSensorReadings(readingsDTOs)
        return readings
    },
    getNewPhoto: async (plcId) => {
        const url = new URL(`${PLC_URL}/${plcId.toString()}/Foto/Scatto`)
        const fotoDTO = await getRequest<FotoDTO>({
            url,
            next: { tags: [PLC_TAG, PHOTO_TAG] },
            cache: 'no-store',
            ...fetchOptions,
        })
        const photo = mapToPhoto(fotoDTO)
        return photo
    },
    getLastPhoto: async (plcId) => {
        const url = new URL(`${PLC_URL}/${plcId.toString()}/Foto/UltimaAcquisizione`)
        const fotoDTO = await getRequest<FotoDTO>({
            url,
            next: { tags: [PLC_TAG, PHOTO_TAG] },
            ...fetchOptions,
        })
        const photo = mapToPhoto(fotoDTO)
        return photo
    },
    getManyPhotos: async (plcId, itemsPerPage, options) => {
        const url = new URL(`${PLC_URL}/${plcId.toString()}/Foto/Pagination`)
        itemsPerPage && url.searchParams.append('PageSize', itemsPerPage)
        options?.page && url.searchParams.append('PageIndex', options.page)
        options?.order && url.searchParams.append('Descending', options.order === 'desc' ? 'true' : 'false')
        options?.sort && url.searchParams.append('SortBy', capitalizeFirstLetter(options.sort))
        options?.timestampFrom && url.searchParams.append('TimestampFrom', options.timestampFrom)
        options?.timestampTo && url.searchParams.append('TimestampTo', options.timestampTo)

        options?.search && url.searchParams.append('Filters[search]', options.search)

        const fotoPaginationDTO = await getRequest<FotoPaginationDTO>({
            url,
            next: { tags: [PLC_TAG, PHOTO_TAG], revalidate: 300 },
            ...fetchOptions,
        })
        const photosWithPagination = mapToPhotosWithPagination(fotoPaginationDTO)
        return photosWithPagination
    },
    putSensoriPredefiniti: async (plc) => {
        const plcDTO = mapToPlcDTO(plc)
        
        const url = new URL(`${PLC_URL}/SensoriPredefiniti`)
        const plcsDTOResponse = await putJsonRequest<PLCDTO>({ url, body: plcDTO, ...fetchOptions })
        return mapToPLC(plcsDTOResponse)
},
    ripristina: async (id) => {
        // Chiede al dispositivo (via backend → MQTT /Manini/GetConfigurazione) di re-inviare la
        // propria configurazione. Il DB viene riscritto in modo asincrono da ConnectIngestion.
        const url = new URL(`${PLC_URL}/${id.toString()}/ripristina`)
        await postJsonRequest<void>({ url, ...fetchOptions })
    },
})

export { PHOTO_TAG, PLC_TAG, plcCRUD, type PLCCrudType, type PLCGetOptions }
